'use client';

import { usePathname, useSearchParams } from 'next/navigation';

export enum SearchParamAction {
  add = 'add',
  remove = 'remove',
}

export const useCustomSearchParams = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const applySearchParams = (obj: Record<string, string>) => {
    const params = new URLSearchParams(obj).toString();
    window.history.pushState({}, '', `${pathname}${params && '?'}${params?.toString()}`);
  };

  const getSearchParams = () => {
    return Object.fromEntries(searchParams.entries());
  };

  const setSearchParam = (key: string, value: string) => {
    const objSearchParams = getSearchParams();

    objSearchParams[key] = value;

    applySearchParams(objSearchParams);
  };

  const setSearchParams = (params: { key: string; value: string }[]) => {
    const objSearchParams = getSearchParams();

    params.forEach(({ key, value }) => {
      objSearchParams[key] = value;
    });

    applySearchParams(objSearchParams);
  };

  const removeSearchParam = (key: string) => {
    const objSearchParams = getSearchParams();

    Reflect.deleteProperty(objSearchParams, key);

    applySearchParams(objSearchParams);
  };

  const updateSearchParams = (items: { action: SearchParamAction; key: string; value?: string }[]) => {
    const objSearchParams = getSearchParams();

    items.forEach(({ action, key, value }) => {
      switch (action) {
        case SearchParamAction.add:
          if (value) {
            objSearchParams[key] = value;
          }
          break;
        case SearchParamAction.remove:
          Reflect.deleteProperty(objSearchParams, key);
          break;
        default:
          break;
      }
    });

    applySearchParams(objSearchParams);
  };

  const clearSearchParams = () => {
    applySearchParams({});
  };

  const setRawParams = (params: string) => {
    window.history.pushState({}, '', `${pathname}${params && '?'}${params?.toString()}`);
  };

  return {
    searchParams,
    setSearchParams,
    setSearchParam,
    removeSearchParam,
    updateSearchParams,
    clearSearchParams,
    setRawParams,
  };
};
